import {
  ChannelIcon,
  channelColors,
  MessageSquareMoreIcon,
  Heading,
  Link,
  Text,
} from '@buffer-mono/popcorn'

import { FeedbackWidget } from '~publish/components/FeedbackWidget'
import { getChannelName, isService } from '~publish/helpers/channels'
import { ConnectSupportedChannelsCard } from '~publish/pages/Community/components/EmptyStates/ConnectSupportedChannelsCard'
import type { ChannelType } from '~publish/pages/Community/types'
import { isComingSoonService } from '~publish/pages/Community/util/comingSoonServices'
import { isNeverSupportedService } from '~publish/pages/Community/util/neverSupportedServices'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'

import styles from './SingleChannelNotAvailable.module.css'

import type { CSSProperties, JSX } from 'react'

const IG_CREATOR_BUSINESS_HELP_URL =
  'https://support.buffer.com/articles/using-instagram-with-buffer-YSjg2dXFV8'

type Variant =
  | 'comingSoon'
  | 'facebookGroup'
  | 'igPersonal'
  | 'neverSupported'
  | 'notSupported'

function getVariant(channel: ChannelType): Variant {
  if (isComingSoonService(channel.service)) return 'comingSoon'
  // Detected from `type`, not `serverUrl`: a group's URL isn't always in the
  // `www.facebook.com/groups` form, and missing a group falls through to
  // "...for Facebook yet", which is wrong because Facebook Pages are supported.
  if (channel.service === 'facebook' && channel.type === 'group')
    return 'facebookGroup'
  if (channel.service === 'instagram' && channel.type === 'profile')
    return 'igPersonal'
  if (isNeverSupportedService(channel.service)) return 'neverSupported'
  return 'notSupported'
}

// Facebook groups and personal profiles get a more specific display name than
// the bare service name: Facebook Pages are supported, so "...for Facebook"
// would be misleading.
function getDisplayChannelName(channel: ChannelType): string {
  if (channel.type === 'group' && channel.service === 'facebook')
    return 'Facebook groups'
  if (channel.service === 'facebook' && channel.type === 'profile')
    return 'Facebook Personal Profiles'
  return isService(channel.service)
    ? getChannelName(channel.service)
    : channel.service
}

export function SingleChannelNotAvailable({
  channel,
}: {
  channel: ChannelType
}): JSX.Element {
  const variant = getVariant(channel)
  const channelName = getDisplayChannelName(channel)

  return (
    <div className={styles.container}>
      <div className={styles.emptyState}>
        {variant === 'comingSoon' ? (
          <BrandedIconBubble service={channel.service} />
        ) : (
          <NeutralIconBubble />
        )}
        <div className={styles.copy}>
          {variant === 'comingSoon' && (
            <ComingSoonCopy channelName={channelName} />
          )}
          {variant === 'facebookGroup' && (
            <FacebookGroupCopy
              channelName={channelName}
              service={channel.service}
            />
          )}
          {variant === 'igPersonal' && <IgPersonalCopy />}
          {variant === 'neverSupported' && (
            <NeverSupportedCopy channelName={channelName} />
          )}
          {variant === 'notSupported' && (
            <NotSupportedCopy
              channelName={channelName}
              service={channel.service}
            />
          )}
        </div>
      </div>
      <ConnectSupportedChannelsCard
        hint={
          variant === 'comingSoon'
            ? 'In the meantime, connect one of the supported channels to start interacting with your audience.'
            : 'Connect one of the supported channels to start interacting with your audience.'
        }
      />
    </div>
  )
}

function NeutralIconBubble(): JSX.Element {
  return (
    <div className={styles.iconBubble}>
      <MessageSquareMoreIcon size="medium" color="subtle" />
    </div>
  )
}

type BubbleStyle = CSSProperties & {
  '--bubble-color': string
}

function BrandedIconBubble({ service }: { service: string }): JSX.Element {
  const bubbleColor = isService(service)
    ? channelColors[service]
    : 'var(--color-bg-a-subtle)'
  const style: BubbleStyle = { '--bubble-color': bubbleColor }
  return (
    <div
      className={`${styles.iconBubble} ${styles.brandedIconBubble}`}
      style={style}
    >
      {isChannelIconService(service) && (
        <ChannelIcon type={service} color="branded" size="medium" />
      )}
    </div>
  )
}

function ComingSoonCopy({ channelName }: { channelName: string }): JSX.Element {
  return (
    <>
      <Heading as="h2" size="sm" align="center" className={styles.heading}>
        {channelName} coming soon to Community!
      </Heading>
      <Text color="subtle">
        Very soon you&apos;ll be able to reply to your {channelName} comments
        from this single, distraction-free inbox.
      </Text>
    </>
  )
}

function NeverSupportedCopy({
  channelName,
}: {
  channelName: string
}): JSX.Element {
  return (
    <Heading as="h2" size="sm" align="center" className={styles.heading}>
      Community is not supported for {channelName}.
    </Heading>
  )
}

function IgPersonalCopy(): JSX.Element {
  return (
    <>
      <Heading as="h2" size="sm" align="center" className={styles.heading}>
        Community isn&apos;t available for Instagram Personal Profiles
      </Heading>
      <Text color="subtle">
        <Link
          href={IG_CREATOR_BUSINESS_HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Switch to a Creator or Business
        </Link>{' '}
        account to start using Community for Instagram.
      </Text>
    </>
  )
}

function FacebookGroupCopy({
  channelName,
  service,
}: {
  channelName: string
  service: string
}): JSX.Element {
  return (
    <>
      <Heading as="h2" size="sm" align="center" className={styles.heading}>
        Community isn&apos;t available for Facebook groups
      </Heading>
      <AddChannelFeedback channelName={channelName} service={service} />
    </>
  )
}

function NotSupportedCopy({
  channelName,
  service,
}: {
  channelName: string
  service: string
}): JSX.Element {
  return (
    <>
      <Heading as="h2" size="sm" align="center" className={styles.heading}>
        Community isn&apos;t available for {channelName} yet.
      </Heading>
      <AddChannelFeedback channelName={channelName} service={service} />
    </>
  )
}

function AddChannelFeedback({
  channelName,
  service,
}: {
  channelName: string
  service: string
}): JSX.Element {
  return (
    <Text color="subtle">
      We&apos;re actively working on adding more channels. Would you like us to
      add {channelName}?{' '}
      <FeedbackWidget
        id={`comments-not-supported-${service}`}
        source="comments"
        initialSummary={`Would love to use ${channelName} in Community ✨`}
        align="center"
      >
        <button type="button" className={styles.feedbackTrigger}>
          Leave us a note
        </button>
      </FeedbackWidget>
    </Text>
  )
}
